import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AgentAvatar, ConfidentialityBadge } from "@/components/Badges";
import { postDownload } from "@/lib/download";
import { Plus, Send, Loader2, Sparkles, ShieldCheck, ChevronDown, GitMerge, Workflow, Paperclip, X, FileText, FileType } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Le prix du sucre a fortement augmenté à N'Djaména, prépare une note d'alerte au Ministre.",
  "Prépare le brief du jour pour le Secrétaire général.",
  "Rédige un projet d'arrêté sur la normalisation des huiles alimentaires.",
  "Prépare une note de position pour la négociation ZLECAf.",
];

function RoutingBanner({ m }) {
  if (!m.pilot) return null;
  return (
    <div className="mb-2" data-testid="routing-chips">
      {m.workflow && (
        <div className="flex items-center gap-1.5 text-[11px] text-navy font-medium mb-1.5">
          <Workflow className="h-3.5 w-3.5" strokeWidth={1.5} />
          <span>Workflow : {m.workflow}</span>
          {m.livrable && m.livrable !== "—" && <span className="text-muted-foreground">· Livrable : {m.livrable}</span>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Agents mobilisés :</span>
        <span className="rounded-sm bg-navy text-white text-[11px] px-2 py-0.5 font-medium">Pilote · {m.pilot}</span>
        {m.associates?.map((a) => (
          <span key={a} className="rounded-sm bg-slate-100 text-slate-700 text-[11px] px-2 py-0.5">{a}</span>
        ))}
        {m.confidentialite && <ConfidentialityBadge level={m.confidentialite} />}
      </div>
    </div>
  );
}

function Contributions({ items }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-2 border border-border rounded-sm bg-slate-50/60" data-testid="contributions-panel">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-3 py-2 text-[11px] font-semibold text-slate-600 hover:text-navy" data-testid="contributions-toggle">
        <span className="flex items-center gap-1.5"><GitMerge className="h-3.5 w-3.5" /> Contributions par agent ({items.length})</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          {items.map((c) => (
            <div key={c.agent} className="border-l-2 border-navy/30 pl-3" data-testid={`contribution-${c.agent}`}>
              <div className="flex items-center gap-2 mb-1">
                <AgentAvatar agentKey={c.agent} size={20} />
                <span className="text-[11px] font-semibold text-navy">{c.agent}</span>
              </div>
              <p className="text-[12px] text-slate-600 whitespace-pre-wrap font-mono leading-snug">{c.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Chat() {
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [consolide, setConsolide] = useState(true);
  const [pending, setPending] = useState([]); // pending attachments {id, original_filename}
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const startConversation = useCallback(async () => {
    const { data } = await api.post("/conversations", {});
    setConversationId(data.id);
    setMessages([]);
    setPending([]);
  }, []);

  useEffect(() => { startConversation(); }, [startConversation]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const uploadAttachment = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPending((p) => [...p, { id: data.id, original_filename: data.original_filename }]);
      toast.success(`« ${data.original_filename} » joint`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi du fichier");
    } finally {
      setUploading(false);
    }
  };

  const exportMessage = async (m, fmt) => {
    try {
      await postDownload("/export/text", { title: `Livrable ${m.pilot || "SG CORE"}`, content: m.content, format: fmt }, `livrable_sg_core.${fmt}`);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error("Échec de l'export");
    }
  };

  const patchLast = (patch) => {
    setMessages((m) => {
      const copy = [...m];
      const last = copy[copy.length - 1];
      if (!last) return copy;
      copy[copy.length - 1] = typeof patch === "function" ? patch(last) : { ...last, ...patch };
      return copy;
    });
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if ((!content && pending.length === 0) || streaming || !conversationId) return;
    setInput("");
    const fileIds = pending.map((p) => p.id);
    const attached = pending;
    setPending([]);
    const baseId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}`;
    setMessages((m) => [...m,
      { id: `u-${baseId}`, role: "user", content, attachments: attached },
      { id: `a-${baseId}`, role: "assistant", pilot: null, associates: [], workflow: null, livrable: null, confidentialite: null, contributions: [], status: "", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("sg_token")}` },
        body: JSON.stringify({ conversation_id: conversationId, message: content || "(voir pièces jointes)", mode: consolide ? "consolide" : "direct", agent_key: "PARAFIX", file_ids: fileIds }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          let e;
          try { e = JSON.parse(line); } catch { continue; }
          if (e.type === "routing") {
            patchLast({ pilot: e.pilot, associates: e.associates, workflow: e.workflow, livrable: e.livrable, confidentialite: e.confidentialite });
          } else if (e.type === "status") {
            patchLast({ status: e.message });
          } else if (e.type === "contribution") {
            patchLast((last) => ({ ...last, contributions: [...last.contributions, { agent: e.agent, content: e.content }] }));
          } else if (e.type === "delta") {
            patchLast((last) => ({ ...last, status: "", content: (last.content || "") + e.content }));
          } else if (e.type === "done" || e.type === "final") {
            if (e.confidentialite) patchLast({ confidentialite: e.confidentialite });
          }
        }
      }
    } catch (err) {
      patchLast((last) => ({ ...last, content: (last.content || "") + `\n[Erreur de connexion: ${err.message}]` }));
    } finally {
      setStreaming(false);
      patchLast({ status: "" });
    }
  };

  return (
    <>
      <PageHeader
        title="Chat — SG CORE / PARAFIX"
        subtitle="Porte d'entrée du cabinet · orchestration multi-agents · réponse consolidée"
        actions={
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" title="Mobilise pilote + agents associés puis consolide">
              <Switch checked={consolide} onCheckedChange={setConsolide} data-testid="consolidation-toggle" />
              Consolidation multi-agents
            </label>
            <Button onClick={startConversation} variant="outline" className="rounded-sm" data-testid="new-conversation">
              <Plus className="h-4 w-4 mr-1.5" /> Nouvelle saisine
            </Button>
          </div>
        }
      />
      <div className="flex flex-col h-[calc(100vh-81px)]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin px-8 py-6" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="max-w-2xl mx-auto text-center pt-12 animate-fade-up">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-navy text-gold mb-4">
                <ShieldCheck className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl font-bold text-navy">Bonjour, Monsieur le Secrétaire général.</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-8">
                Soumettez une saisine. SG CORE qualifie, route via la matrice des workflows, mobilise les agents compétents et PARAFIX consolide une réponse unique.
              </p>
              <div className="grid sm:grid-cols-2 gap-2 text-left">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid="chat-suggestion"
                    className="flex items-start gap-2 border border-border rounded-sm p-3 text-sm text-slate-700 hover:border-navy hover:bg-slate-50 transition-colors">
                    <Sparkles className="h-4 w-4 text-navy shrink-0 mt-0.5" strokeWidth={1.5} />
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="max-w-3xl mx-auto space-y-6">
            {messages.map((m, i) => (
              <div key={m.id || i} className="animate-fade-up" data-testid={`message-${m.role}`}>
                {m.role === "user" ? (
                  <div className="flex justify-end">
                    <div className="max-w-[80%]">
                      <div className="bg-navy text-white rounded-sm px-4 py-2.5 text-sm leading-relaxed">{m.content}</div>
                      {m.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 justify-end" data-testid="user-attachments">
                          {m.attachments.map((f) => (
                            <span key={f.id} className="flex items-center gap-1 text-[11px] bg-slate-100 text-slate-600 rounded-sm px-2 py-0.5">
                              <Paperclip className="h-3 w-3" /> {f.original_filename}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <AgentAvatar agentKey={m.pilot || "PARAFIX"} size={36} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-navy">SG CORE</span>
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Réponse consolidée</span>
                      </div>
                      <RoutingBanner m={m} />
                      <Contributions items={m.contributions} />
                      {m.status && (
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-2" data-testid="orchestration-status">
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-navy" /> {m.status}
                        </div>
                      )}
                      {(m.content || (!m.status && streaming && i === messages.length - 1)) && (
                        <div className="bg-white border border-border rounded-sm px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap text-slate-800 font-mono">
                          {m.content || <Loader2 className="h-4 w-4 animate-spin text-navy" />}
                        </div>
                      )}
                      {m.content && !streaming && (
                        <div className="flex items-center gap-2 mt-2" data-testid="message-export">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Exporter :</span>
                          <button onClick={() => exportMessage(m, "pdf")} className="flex items-center gap-1 text-[11px] text-navy hover:underline" data-testid="export-msg-pdf"><FileText className="h-3.5 w-3.5" /> PDF</button>
                          <button onClick={() => exportMessage(m, "docx")} className="flex items-center gap-1 text-[11px] text-navy hover:underline" data-testid="export-msg-docx"><FileType className="h-3.5 w-3.5" /> Word</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border bg-white px-8 py-4">
          <div className="max-w-3xl mx-auto">
            {pending.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="pending-attachments">
                {pending.map((f) => (
                  <span key={f.id} className="flex items-center gap-1 text-[11px] bg-slate-100 text-slate-700 rounded-sm px-2 py-1">
                    <Paperclip className="h-3 w-3" /> {f.original_filename}
                    <button onClick={() => setPending((p) => p.filter((x) => x.id !== f.id))} className="ml-1 text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" className="hidden" data-testid="chat-file-input"
                onChange={(e) => { uploadAttachment(e.target.files[0]); e.target.value = ""; }} />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="rounded-sm h-[52px] px-3" data-testid="chat-attach" title="Joindre un fichier (PDF, Word, Excel, image)">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              </Button>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Rédigez votre saisine… (Entrée pour envoyer, Maj+Entrée pour un retour à la ligne)"
                className="rounded-sm resize-none min-h-[52px] max-h-40 font-sans"
                data-testid="chat-input"
              />
              <Button onClick={() => send()} disabled={streaming || (!input.trim() && pending.length === 0)}
                className="rounded-sm bg-navy hover:bg-navy-dark h-[52px] px-4" data-testid="chat-send">
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
